/**
 * The filter rail.
 *
 * Controls are disabled rather than hidden for Preview accounts: seeing what
 * a paid plan unlocks is the point of a preview. The server ignores these
 * parameters for free tokens regardless of what the DOM allows.
 */

import { useState } from "react";

import type { Capabilities } from "../api/types";
import {
  alertReadiness,
  describeFilters,
  resolveAlertName,
  suggestName,
} from "../lib/alerts";
import type { Filters, View } from "../lib/filters";

const FLAG_OPTIONS = [
  { value: "", label: "Any" },
  { value: "unsafe_structure", label: "Unsafe structure" },
  { value: "lien_recorded", label: "Violation lien" },
  { value: "homestead_lost", label: "Homestead dropped" },
  { value: "expired_permit", label: "Expired permit" },
  { value: "absentee", label: "Absentee owner" },
  { value: "milestone_due", label: "Milestone due" },
];

const RATE_OPTIONS = [
  { value: 0, label: "Any" },
  { value: 0.1, label: "10%+" },
  { value: 0.25, label: "25%+" },
  { value: 0.5, label: "50%+" },
];

const SORT_OPTIONS = [
  { value: "score", label: "Distress score" },
  { value: "rate", label: "Concentration" },
  { value: "age", label: "Age" },
  { value: "units", label: "Size" },
];

interface Props {
  view: View;
  filters: Filters;
  capabilities: Capabilities;
  onChange: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  /** Saves the current filters as an alert. Resolves when the server has it. */
  onSaveAlert: (name: string) => Promise<void>;
  /** How many alerts already exist, for the cap. */
  alertCount: number;
  /** The open county's name, for the suggested alert name. */
  countyName: string;
}

export function FilterRail({
  view,
  filters,
  capabilities,
  onChange,
  onSaveAlert,
  alertCount,
  countyName,
}: Props) {
  const locked = !capabilities.can_filter;

  // The alert *is* this search, so what it will match is described from the
  // same filter object rather than from a second summary that could disagree.
  const describing = describeFilters(filters, view);
  const [alertName, setAlertName] = useState("");
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertNote, setAlertNote] = useState("");
  // The suggestion is a *placeholder*, never the value. As the value it made
  // the field impossible to clear — emptying it refilled the box from the
  // fallback — so replacing it meant backspacing through it, and a half-deleted
  // suggestion is what got saved. It also stays live this way: change a filter
  // and the suggestion follows, which seeding the state once would not do.
  const suggested = suggestName(countyName, describing);
  const name = resolveAlertName(alertName, suggested);
  const alertOk = alertReadiness(capabilities.can_watch, alertCount, name);

  async function saveAlert() {
    setSavingAlert(true);
    setAlertNote("");
    try {
      await onSaveAlert(name);
      setAlertName("");
      setAlertNote("Saved. It reports what appears from now on.");
    } catch (cause) {
      setAlertNote(cause instanceof Error ? cause.message : "Could not save.");
    } finally {
      setSavingAlert(false);
    }
  }

  return (
    <aside className="rail">
      <div className="grp">
        <h3>Find</h3>
        <label className="field">
          <span>Address, owner or folio</span>
          <input
            value={filters.search}
            placeholder="e.g. NE 2 AVE"
            disabled={locked}
            onChange={(e) => onChange("search", e.target.value)}
          />
        </label>
      </div>

      {view === "buildings" ? (
        <div className="grp">
          <h3>Building</h3>
          <label className="check">
            <input
              type="checkbox"
              checked={filters.milestone_only}
              disabled={locked}
              onChange={(e) => onChange("milestone_only", e.target.checked)}
            />
            <span>Milestone overdue</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={filters.unsafe_only}
              disabled={locked}
              onChange={(e) => onChange("unsafe_only", e.target.checked)}
            />
            <span>Has unsafe-structure case</span>
          </label>

          <label className="field">
            <span>Min distress rate</span>
            <select
              value={filters.min_rate}
              disabled={locked}
              onChange={(e) => onChange("min_rate", Number(e.target.value))}
            >
              {RATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Sort by</span>
            <select
              value={filters.order}
              disabled={locked}
              onChange={(e) => onChange("order", e.target.value)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="grp">
          <h3>Signal</h3>
          <label className="field">
            <span>Flag</span>
            <select
              value={filters.flag}
              disabled={locked}
              onChange={(e) => onChange("flag", e.target.value)}
            >
              {FLAG_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Min score</span>
            <input
              type="number"
              min={0}
              step={5}
              value={filters.min_score}
              disabled={locked}
              onChange={(e) =>
                onChange("min_score", Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
      )}

      <div className="grp">
        <h3>Alert</h3>
        <label className="field">
          <span>Name</span>
          <input
            value={alertName}
            placeholder={suggested}
            disabled={!capabilities.can_watch || savingAlert}
            onChange={(e) => setAlertName(e.target.value)}
          />
        </label>
        {/* What it will actually match, said before it is saved. The commonest
            way to end up with a standing query that reports the whole county
            forever is to save one without noticing it has no filters. */}
        <p className="sub">
          {describing.length
            ? `Watches: ${describing.join(", ")}.`
            : "No filters set — this would watch every new row in the county."}
        </p>
        <button
          className="btn ghost"
          disabled={savingAlert || !alertOk.allowed}
          title={alertOk.reason || undefined}
          onClick={() => void saveAlert()}
        >
          {savingAlert ? "Saving…" : "Save as alert"}
        </button>
        {/* The reason in text as well as the title: a disabled button whose
            explanation lives only in a tooltip is unreachable by touch and by
            keyboard. */}
        {alertOk.reason && <p className="sub">{alertOk.reason}</p>}
        {alertNote && <p className="sub">{alertNote}</p>}
      </div>

    </aside>
  );
}
